import { Validators } from '@angular/forms';
import {
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  DEFAULT_CANCELLATION_FEE_ATTRIBUTION,
  DepositSettings,
  LegacyDepositValueNormalization,
} from '@principle-theorem/principle-core';
import {
  CancellationFeeAttribution,
  DEPOSIT_BOOKING_CONTEXTS,
  DepositBookingContext,
  DepositCaptureStrategy,
  DepositForfeiturePolicy,
  type IDepositForfeitureRule,
  type IDepositSettings,
  type ISchedulingEventReason,
} from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  TypeGuard,
  type DocumentReference,
} from '@principle-theorem/shared';
import { DepositAmountConversion } from './deposit-amount-conversion';

/**
 * Mirrors the `Number.isInteger` requirement of the deposit type guards so a
 * decimal is rejected inline instead of failing `isDepositSettings` on save.
 */
export const WHOLE_NUMBER_PATTERN = /^\d+$/;

type DepositSettingsFormValue = Omit<
  IDepositSettings,
  | 'treatmentConfigurationDeposits'
  | 'treatmentTemplateOverrides'
  | 'patientDepositAmountCents'
> & {
  patientDepositAmountDollars?: number;
};

/**
 * Persisted deposit settings as read back from a brand, whose capture strategy
 * may be a value since removed from the enum.
 */
interface IPersistedDepositSettings
  extends Omit<IDepositSettings, 'captureStrategy'> {
  captureStrategy: string;
}

export class DepositForfeitureRuleFormGroup extends TypedFormGroup<IDepositForfeitureRule> {
  constructor(rule: IDepositForfeitureRule) {
    super({
      reasonRef: new TypedFormControl(rule.reasonRef, Validators.required),
      forfeiturePercentage: new TypedFormControl(rule.forfeiturePercentage, [
        Validators.required,
        Validators.min(1),
        Validators.max(100),
        Validators.pattern(WHOLE_NUMBER_PATTERN),
      ]),
    });
  }
}

export class DepositForfeitureRulesFormArray extends TypedFormArray<IDepositForfeitureRule> {
  declare controls: DepositForfeitureRuleFormGroup[];
}

export class DepositSettingsFormGroup extends TypedFormGroup<DepositSettingsFormValue> {
  private _allowsMissingCancellationPolicyUrl = false;
  private _cancellationPolicyUrlGrandfatheringExpired = false;
  declare controls: TypedFormGroup<DepositSettingsFormValue>['controls'] & {
    forfeitureRules: DepositForfeitureRulesFormArray;
  };

  constructor() {
    super({
      enabled: new TypedFormControl<boolean>(false),
      appliesTo: new TypedFormControl<DepositBookingContext[] | undefined>(
        [...DEPOSIT_BOOKING_CONTEXTS],
        Validators.required
      ),
      captureStrategy: new TypedFormControl<DepositCaptureStrategy>(
        DepositCaptureStrategy.ChargeOnBookingRequest,
        Validators.required
      ),
      forfeitureRules: new DepositForfeitureRulesFormArray(),
      forfeiturePolicy: new TypedFormControl<DepositForfeiturePolicy>(
        DepositForfeiturePolicy.Auto,
        Validators.required
      ),
      cancellationFeeAttribution: new TypedFormControl<
        CancellationFeeAttribution | undefined
      >(DEFAULT_CANCELLATION_FEE_ATTRIBUTION, Validators.required),
      forfeitureEmailEnabled: new TypedFormControl<boolean>(false),
      refundConfirmationEmailEnabled: new TypedFormControl<boolean>(false),
      autoApplyAppointmentAccountCredits: new TypedFormControl<boolean>(false),
      patientDepositAmountDollars: new TypedFormControl<number | undefined>(
        undefined,
        Validators.min(0.01)
      ),
      cancellationPolicyUrl: new TypedFormControl<string | undefined>(
        undefined
      ),
    });
  }

  private static _readCaptureStrategy(
    captureStrategy: string
  ): DepositCaptureStrategy {
    const normalizedCaptureStrategy =
      LegacyDepositValueNormalization.captureStrategy(captureStrategy);
    return TypeGuard.enumValue(DepositCaptureStrategy)(
      normalizedCaptureStrategy
    )
      ? normalizedCaptureStrategy
      : DepositCaptureStrategy.ChargeOnBookingRequest;
  }

  get allowsMissingCancellationPolicyUrl(): boolean {
    return this._allowsMissingCancellationPolicyUrl;
  }

  updateCancellationPolicyUrlValidators(): void {
    const control = this.controls.cancellationPolicyUrl;
    const linkIsSent = this.controls.forfeitureEmailEnabled.value === true;
    control.setValidators(
      linkIsSent && !this._allowsMissingCancellationPolicyUrl
        ? [Validators.required]
        : []
    );
    control.updateValueAndValidity({ emitEvent: false });
    if (!linkIsSent) {
      this._cancellationPolicyUrlGrandfatheringExpired =
        this._cancellationPolicyUrlGrandfatheringExpired ||
        this._allowsMissingCancellationPolicyUrl;
      this._allowsMissingCancellationPolicyUrl = false;
    }
  }

  selectForfeitureReason(
    reasonRef: DocumentReference<ISchedulingEventReason>
  ): void {
    if (this.findForfeitureRule(reasonRef)) {
      return;
    }
    this.controls.forfeitureRules.push(
      new DepositForfeitureRuleFormGroup({
        reasonRef,
        forfeiturePercentage: 100,
      })
    );
  }

  deselectForfeitureReason(
    reasonRef: DocumentReference<ISchedulingEventReason>
  ): void {
    const ruleIndex = this.controls.forfeitureRules.controls.findIndex(
      (rule) => {
        const selectedReasonRef = rule.controls.reasonRef.value;
        return !!selectedReasonRef && isSameRef(selectedReasonRef, reasonRef);
      }
    );
    if (ruleIndex === -1) {
      return;
    }
    this.controls.forfeitureRules.removeAt(ruleIndex);
  }

  /**
   * The capture strategy is normalized on read because branch-only development
   * brands may still hold a removed enum value, which would select no option in
   * the timing dropdown and be written straight back out on the next save.
   */
  patchSettings(settings: IPersistedDepositSettings): void {
    const normalizedSettings = DepositSettings.normalizeForRead({
      ...settings,
      captureStrategy: DepositSettingsFormGroup._readCaptureStrategy(
        settings.captureStrategy
      ),
    });
    this.setForfeitureRules(normalizedSettings.forfeitureRules);
    this.patchValue(
      {
        ...normalizedSettings,
        appliesTo: normalizedSettings.appliesTo?.length
          ? normalizedSettings.appliesTo
          : [...DEPOSIT_BOOKING_CONTEXTS],
        cancellationFeeAttribution:
          normalizedSettings.cancellationFeeAttribution ??
          DEFAULT_CANCELLATION_FEE_ATTRIBUTION,
        patientDepositAmountDollars: DepositAmountConversion.dollarsFromCents(
          normalizedSettings.patientDepositAmountCents
        ),
      },
      { emitEvent: false }
    );
    this._allowsMissingCancellationPolicyUrl =
      !this._cancellationPolicyUrlGrandfatheringExpired &&
      normalizedSettings.forfeitureEmailEnabled &&
      normalizedSettings.cancellationPolicyUrl === undefined;
  }

  toSettings(): Omit<IDepositSettings, 'treatmentTemplateOverrides'> {
    const { cancellationPolicyUrl, patientDepositAmountDollars, ...settings } =
      this.getRawValue();
    const patientDepositAmountCents = DepositAmountConversion.centsFromDollars(
      patientDepositAmountDollars
    );

    return {
      ...settings,
      ...(cancellationPolicyUrl ? { cancellationPolicyUrl } : {}),
      ...(patientDepositAmountCents ? { patientDepositAmountCents } : {}),
    };
  }

  setForfeitureRules(rules: IDepositForfeitureRule[]): void {
    this.controls.forfeitureRules.clear({ emitEvent: false });
    for (const rule of rules) {
      this.controls.forfeitureRules.push(
        new DepositForfeitureRuleFormGroup(rule),
        { emitEvent: false }
      );
    }
  }

  findForfeitureRule(
    reasonRef: DocumentReference<ISchedulingEventReason>
  ): DepositForfeitureRuleFormGroup | undefined {
    return this.controls.forfeitureRules.controls.find((rule) => {
      const selectedReasonRef = rule.controls.reasonRef.value;
      return !!selectedReasonRef && isSameRef(selectedReasonRef, reasonRef);
    });
  }
}
